import React, { forwardRef, useEffect, useImperativeHandle } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { ProjectFormProps } from './ProjectFormProps';
import { Stack } from '@mui/material';
import Button from '@ingka/button';
import useTranslation from '../../hooks/useTranslation';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch } from 'react-redux';
import { setSolutions } from '../../store/projects/actionWrapper';
import { enqueueErrorSnackbar } from '../../store/notifications/actionWrapper';
import { ProjectRoles, ProjectStatus } from '../../models/project';
import { returnLockedFields } from '../../utils/fieldEditingRules';
import { useTypedSelector } from '../../store';
import { selectUserRole } from '../../store/userData/selectors';
import NotSyncedWithEtr from '../NotSyncedWithETR';

const FULL_WIDTH_STYLE = { width: '100%' };
export type ProjectFormRef = {
  submit: () => void;
};

const ProjectForm = forwardRef<ProjectFormRef, ProjectFormProps>(
  (
    {
      onSubmit,
      project,
      setFormModified,
      onClose,
      children,
      submitBtnTitle,
      selectedSiteLocation,
      validationSchema,
      isEdit,
      country,
      setFormErrors,
      disableRestrictions,
      readOnly,
      directiveOpen,
      onDirectiveBack,
    },
    ref,
  ) => {
    const t = useTranslation();
    const dispatch = useDispatch();
    const role = useTypedSelector(selectUserRole);
    const onValidationFailed = () => {
      dispatch(
        enqueueErrorSnackbar('Please fill in all of the required fields'),
      );
    };

    const formMethods = useForm({
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      resolver: yupResolver(validationSchema),
      mode: 'onChange',
      defaultValues: { ...project, disabledFields: [] },
    });

    const {
      reset,
      handleSubmit,
      setValue,
      formState: { isDirty, dirtyFields, errors },
    } = formMethods;

    useImperativeHandle(
      ref,
      () => ({
        submit: () => {
          handleSubmit(onSubmit, onValidationFailed)();
        },
      }),
      [handleSubmit],
    );
    useEffect(() => {
      let updatedProject: { [key: string]: unknown } = {};
      if (project) {
        updatedProject =
          project.projectResponsibleList?.reduce(
            (acc, el) => ({
              ...acc,
              [ProjectRoles[el.projectResponsibleRole]]:
                el.projectResponsiblePerson,
            }),
            { ...project },
          ) || project;

        if (updatedProject.isLeasedFromAnotherIngkaCompany != null) {
          updatedProject.isLeasedFromAnotherIngkaCompany = String(
            updatedProject.isLeasedFromAnotherIngkaCompany,
          );
        }

        reset({
          ...updatedProject,
          disabledFields: returnLockedFields(
            project?.projectSource,
            project?.isep,
            project?.establishmentType,
            role,
            project?.unitCategory && project?.unitType,
            disableRestrictions,
            readOnly,
          ),
        });
        setFormModified(false);
        dispatch(setSolutions(project?.solutions || []));
      }
      if (!isEdit) {
        setValue('projectStatus', ProjectStatus.QUALIFIED);
        setValue('confidential', 'INTERNAL');
        setValue('needForAstroDecommissioning', 'NO');
      }
    }, [project]);

    useEffect(() => {
      if (!isEdit) {
        setValue('countryCode', country);
      }
    }, [country]);

    useEffect(() => {
      if (dirtyFields.id) {
        return;
      }
      if (Object.values(dirtyFields).some((field) => field === true)) {
        setFormModified(isDirty);
      }
    }, [isDirty, dirtyFields]);

    useEffect(() => {
      setFormErrors({ ...errors });
    }, [JSON.stringify(errors)]);

    useEffect(() => {
      if (selectedSiteLocation) {
        setValue('city', selectedSiteLocation?.city);
        setValue('address', selectedSiteLocation?.address);
        setValue('state', selectedSiteLocation?.state);
        setValue('locationName', selectedSiteLocation?.locationName);
      }
    }, [selectedSiteLocation]);

    return (
      <FormProvider {...formMethods}>
        <form
          noValidate
          onKeyDown={(e) => {
            const target = e.target as HTMLElement;
            if (e.key === 'Enter' && target.tagName !== 'TEXTAREA') {
              e.preventDefault();
            }
          }}
          onSubmit={handleSubmit(onSubmit, onValidationFailed)}
          style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
        >
          {isEdit && (
            <NotSyncedWithEtr
              synced={project?.etrSynced}
              source={project?.projectSource}
            />
          )}
          <Stack flex={1}>{children}</Stack>
          <Stack
            width={1}
            direction={'row'}
            p={4}
            spacing={2}
            justifyContent={'center'}
            sx={{
              position: 'sticky',
              bottom: 0,
              backgroundColor: '#ffffff',
              zIndex: 1,
            }}
          >
            {directiveOpen ? (
              <Button
                style={FULL_WIDTH_STYLE}
                data-testid={'close-directive-button'}
                text={t('common.close')}
                type="primary"
                onClick={onDirectiveBack}
              />
            ) : (
              <>
                <Button
                  style={FULL_WIDTH_STYLE}
                  data-testid={'close-project-modal-button'}
                  text={t('common.cancel')}
                  type="secondary"
                  onClick={onClose}
                />
                {!readOnly && (
                  <Button
                    style={FULL_WIDTH_STYLE}
                    data-testid={'submit-project-modal-button'}
                    text={submitBtnTitle}
                    type="primary"
                    htmlType="submit"
                  />
                )}
              </>
            )}
          </Stack>
        </form>
      </FormProvider>
    );
  },
);

export default ProjectForm;
